import type { AppUser, Attempt, AttemptUpdate } from '../types'
import { supabase } from './supabase'

const toDbAttempt = (attempt: Attempt) => ({
  id: attempt.id,
  question_id: attempt.questionId,
  answer: attempt.answer,
  correct: attempt.correct,
  elapsed_seconds: attempt.timeSeconds,
  competency_snapshot: attempt.competency,
  knowledge_snapshot: attempt.knowledge,
  topic_snapshot: attempt.topic,
  variant_group_snapshot: attempt.variantGroup,
  prerequisites_snapshot: attempt.prerequisites,
  confidence: attempt.confidence,
  misconception: attempt.misconception ?? null,
  hint_level: attempt.hintLevel,
  session_type: attempt.session,
  review_due_at: attempt.reviewDueAt ?? null,
  created_at: attempt.at,
})

const fromDbAttempt = (row: Record<string, unknown>): Attempt => ({
  id: String(row.id),
  questionId: String(row.question_id),
  answer: row.answer as Attempt['answer'],
  correct: Boolean(row.correct),
  timeSeconds: Number(row.elapsed_seconds) || 0,
  competency: (row.competency_snapshot as Attempt['competency']) || ['problem-solving'],
  knowledge: (row.knowledge_snapshot as Attempt['knowledge']) || ['algebra'],
  topic: String(row.topic_snapshot || 'Chưa phân loại'),
  variantGroup: String(row.variant_group_snapshot || row.question_id),
  prerequisites: (row.prerequisites_snapshot as Attempt['prerequisites']) || ['algebra'],
  confidence: (row.confidence as Attempt['confidence']) || 'medium',
  misconception: row.misconception as Attempt['misconception'],
  hintLevel: Number(row.hint_level) || 0,
  session: (row.session_type as Attempt['session']) || 'practice',
  at: String(row.created_at),
  reviewDueAt: row.review_due_at ? String(row.review_due_at) : undefined,
  serverPersisted: true,
})

export const attemptRepository = {
  async list(userId: string) {
    if (!supabase) return null
    const { data, error } = await supabase.from('attempts').select('*').eq('user_id', userId).order('created_at', { ascending: true })
    if (error) throw error
    return (data ?? []).map(row => fromDbAttempt(row as Record<string, unknown>))
  },
  async save(userId: string, attempt: Attempt) {
    if (!supabase) return
    const { error } = await supabase.from('attempts').upsert({ user_id: userId, ...toDbAttempt(attempt) })
    if (error) throw error
  },
  async update(userId: string, id: string, update: AttemptUpdate) {
    if (!supabase) return
    const { error } = await supabase.from('attempts').update({ misconception: update.misconception }).eq('id', id).eq('user_id', userId)
    if (error) throw error
  },
}

export const profileRepository = {
  async upsert(user: AppUser) {
    if (!supabase || user.provider !== 'supabase') return
    const { error } = await supabase.from('profiles').upsert({
      id: user.uid,
      full_name: user.displayName,
      nickname: user.nickname,
      avatar: user.avatar,
    })
    if (error) throw error
  },
}
